import { evalConditional } from "@aidkitorg/roboscreener/lib/compute/util";
import * as v0 from "@aidkitorg/types/lib/survey";
import { InfoDict } from "./Questions/Props";

export type ResetDecisionInputs = {
  metadata: v0.LegacyCommonProperties;
  fieldType?: string;
  targetField?: string;
  info: InfoDict;
  viewInfo?: InfoDict;
  /**
   * Fallback reachability check (the field's full visibility conditional), used
   * only for surveys translated before `resetConditional` existed.
   */
  computeEnabled: (info: InfoDict) => boolean | Promise<boolean>;
};

/**
 * Decide whether a `resetUnreachableFields`-flagged field should be reset to "".
 *
 * Prefers the SCOPED `metadata.resetConditional` - which contains only the
 * condition(s) of the Conditional Block(s) that actually declared
 * `resetUnreachableFields` - over the full visibility conditional. This is the fix
 * for the RX Kids bug: a parent gate like `currently_pregnant === 'no'` should NOT
 * wipe `baby_name_1` when the reset block's own condition (the baby-count check) is
 * still satisfied.
 *
 * Falls back to `computeEnabled` (the full conditional) when `resetConditional` is
 * absent, so surveys translated before this field existed keep their prior behavior.
 */
export async function shouldResetUnreachableField(inputs: ResetDecisionInputs): Promise<boolean> {
  const { metadata, fieldType, targetField, info, viewInfo, computeEnabled } = inputs;

  if (!metadata.resetUnreachableFields || !targetField || fieldType === 'Show Field') {
    return false;
  }

  if (metadata.resetConditional) {
    const reachable = await evalConditional(metadata.resetConditional, info, {}, {}, { viewInfo });
    return !reachable;
  }

  // Back-compat: no scoped reset condition available, use the full conditional.
  return (await computeEnabled(info)) === false;
}
